import React from "react";
import styled from "styled-components";
import { useTheme } from "../../contexts/ThemeContext";

interface LogoProps {
  size?: "small" | "medium" | "large";
  variant?: "text" | "icon" | "both" | "minimal";
  /** Show the "Beta" superscript on the text wordmark (default true). */
  showBeta?: boolean;
  className?: string;
}

const LogoContainer = styled.div<{ $size: string }>`
  display: flex;
  align-items: center;
  gap: ${props => props.$size === "large" ? "12px" : props.$size === "medium" ? "10px" : "8px"};
`;

const LogoImage = styled.img<{ $size: string }>`
  width: ${props => props.$size === "large" ? "40px" : props.$size === "medium" ? "28px" : "22px"};
  height: auto;
  object-fit: contain;
`;

/* Heights mirror the website header: 22px desktop wordmark, 18px mobile */
const WordmarkImage = styled.img<{ $size: string }>`
  height: ${props => props.$size === "large" ? "22px" : props.$size === "medium" ? "18px" : "14px"};
  width: auto;
  object-fit: contain;
`;

const MinimalMark = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
  display: block;
`;

/* Shared beta label styles */
const betaLabelCss = `
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  line-height: 1;
`;

/* Superscript for the wordmark (horizontal) variant */
const BetaSup = styled.sup`
  ${betaLabelCss}
  color: ${props => props.theme.brand.primary};
`;

/* Label centred below the minimal (collapsed) icon */
const BetaTag = styled.span`
  ${betaLabelCss}
  color: ${props => props.theme.brand.primary};
  display: block;
  text-align: center;
`;

const WordmarkRow = styled.span`
  display: inline-flex;
  align-items: flex-start;
  gap: 4px;
`;

const MinimalWrap = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: fit-content;
  margin: 0 auto;
`;

export const Logo: React.FC<LogoProps> = ({
  size = "medium",
  variant = "text",
  showBeta = true,
  className
}) => {
  const { isDarkMode } = useTheme();

  // Resolved theme (including "system" mode) picks the asset colour:
  // ink mark/wordmark on light backgrounds, white on dark.
  const logoSrc = isDarkMode ? "/logo-dark.png" : "/logo.png";
  const wordmarkSrc = isDarkMode ? "/wordmark-white.svg" : "/wordmark-dark.svg";

  if (variant === "icon") {
    return (
      <LogoImage
        src={logoSrc}
        alt="Greenback"
        $size={size}
        className={className}
      />
    );
  }

  if (variant === "both") {
    return (
      <LogoContainer $size={size} className={className}>
        <LogoImage src={logoSrc} alt="" $size={size} />
        <WordmarkImage src={wordmarkSrc} alt="Greenback" $size={size} />
      </LogoContainer>
    );
  }

  if (variant === "minimal") {
    return (
      <MinimalWrap className={className}>
        <MinimalMark src={logoSrc} alt="Greenback" />
        <BetaTag>Beta</BetaTag>
      </MinimalWrap>
    );
  }

  return (
    <WordmarkRow className={className}>
      <WordmarkImage src={wordmarkSrc} alt="Greenback" $size={size} />
      {showBeta && <BetaSup>Beta</BetaSup>}
    </WordmarkRow>
  );
};
